import { DEFAULT_STEP_RATIO, TRADE_OVERLAY_TYPE, TRADE_TYPE } from '@/constants/trade';
import { WrappedPosition } from '@/entities/WrappedPosition';
import { BatchOrderSizeDistribution, Side } from '@synfutures/sdks-perp';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

interface TradeState {
  // for limitForm
  tradeSide: Side;
  setTradeSide: (tradeSide: Side) => void;
  orderbookUnitBase: boolean;
  setOrderBookUnitBase: (orderbookUnitBase: boolean) => void;
  hideOtherPair: boolean;
  setHideOtherPair: (hideOtherPair: boolean) => void;
  isUnitInBaseToken: boolean;
  setIsUnitInBaseToken: (isUnitInBaseToken: boolean) => void;
  pnlsharePosition: WrappedPosition | null;
  setSharePnlPosition: (position: WrappedPosition) => void;
  showTradeAreaOverlay?: TRADE_OVERLAY_TYPE;
  setShowTradeAreaOverlay: (showTradeAreaOverlay?: TRADE_OVERLAY_TYPE) => void;
  closePositionPairSymbol?: string;
  setClosePositionPairSymbol: (closePositionPairSymbol?: string) => void;
  tradeSize: string;
  setTradeSize: (tradeSize: string) => void;
  tradeQuoteSize: string;
  setTradeQuoteSize: (tradeQuoteSize: string) => void;
  tradePercent: number;
  limitPrice: string;
  alignedLimitPrice: string;
  setLimitPrice: (limitPrice: string) => void;
  setAlignedLimitPrice: (alignedLimitPrice: string) => void;
  tick: number;
  setTick: (tick: number) => void;
  isTurnOnCrossMarket: boolean;
  setIsTurnOnCrossMarket: (isTurnOnCrossMarket: boolean) => void;
  setTradePercent: (tradePercent: number) => void;
  pairLeverage: { [chainId: number]: { [userAddr: string]: { [pairId: string]: string } } };
  setPairLeverage: (userAddr: string, chainId: number, pairId: string, leverage: string) => void;
  updatePairLeverage: (userAddr: string, chainId: number, pairId: string, leverage: string) => void;
  // for scaleLimitForm
  sizeDistribution: BatchOrderSizeDistribution;
  setSizeDistribution: (sizeDistribution: BatchOrderSizeDistribution) => void;
  upperPrice: string;
  setUpperPrice: (upperPrice: string) => void;
  alignedUpperPrice: string;
  setAlignedUpperPrice: (alignedUpperPrice: string) => void;
  lowerPrice: string;
  setLowerPrice: (lowerPrice: string) => void;
  alignedLowerPrice: string;
  setAlignedLowerPrice: (alignedLowerPrice: string) => void;
  lowerTick: number;
  setLowerTick: (lowerTick: number) => void;
  upperTick: number;
  setUpperTick: (upperTick: number) => void;
  orderCount: number;
  setOrderCount: (orderCount: number) => void;
  stepRatio: number;
  setStepRatio: (stepRatio: number) => void;
  pairStepRatioMap: Record<string, number>;
  setPairStepRatio: (pairKey: string, stepRatio: number) => void;
  scaleLimitStepRatio: number;
  setScaleLimitStepRatio: (scaleLimitStepRatio: number) => void;
  pairScaleLimitStepRatioMap: Record<string, number>;
  setPairScaleLimitStepRatio: (pairKey: string, stepRatio: number) => void;
  tradeType: TRADE_TYPE;
  setTradeType: (tradeType: TRADE_TYPE) => void;
  toLimitTab: boolean;
  setToLimitTab: (fromOrderBook: boolean) => void;
  fromOrderBook: boolean;
  setFromOrderBook: (fromOrderBook: boolean) => void;

  isOpenMobileTradeDrawer: boolean;
  setIsOpenMobileTradeDrawer: (isOpenMobileTradeDrawer: boolean) => void;
}

export const useTradeStore = create<TradeState>()(
  devtools(
    persist(
      (set) => ({
        tradeSide: Side.LONG,
        setTradeSide: (tradeSide) => set(() => ({ tradeSide })),
        orderbookUnitBase: true,
        setOrderBookUnitBase: (orderbookUnitBase) => set(() => ({ orderbookUnitBase })),
        hideOtherPair: false,
        setHideOtherPair: (hideOtherPair) => set(() => ({ hideOtherPair })),
        isUnitInBaseToken: true,
        setIsUnitInBaseToken: (isUnitInBaseToken) => set(() => ({ isUnitInBaseToken })),
        pnlsharePosition: null,
        setSharePnlPosition: (position: WrappedPosition) => set(() => ({ pnlsharePosition: position })),
        showTradeAreaOverlay: undefined,
        setShowTradeAreaOverlay: (showTradeAreaOverlay) => set(() => ({ showTradeAreaOverlay })),
        closePositionPairSymbol: undefined,
        setClosePositionPairSymbol: (closePositionPairSymbol) => set(() => ({ closePositionPairSymbol })),
        tradeSize: '',
        setTradeSize: (tradeSize) => set(() => ({ tradeSize })),
        tradeQuoteSize: '',
        setTradeQuoteSize: (tradeQuoteSize) => set(() => ({ tradeQuoteSize })),
        tradePercent: 0,
        limitPrice: '',
        alignedLimitPrice: '',
        setLimitPrice: (limitPrice) => set(() => ({ limitPrice })),
        setAlignedLimitPrice: (alignedLimitPrice) => set(() => ({ alignedLimitPrice })),
        tick: 0,

        setTick: (tick) => set(() => ({ tick })),
        isTurnOnCrossMarket: true,
        setIsTurnOnCrossMarket: (isTurnOnCrossMarket) => set(() => ({ isTurnOnCrossMarket })),
        setTradePercent: (tradePercent) => {
          set(() => ({ tradePercent: Math.min(tradePercent, 100) }));
        },
        pairLeverage: {},
        setPairLeverage: (userAddr, chainId, pairId, leverage) => {
          set((state) => {
            const newPairLeverage = {
              ...state.pairLeverage,
              [chainId]: {
                ...state.pairLeverage[chainId],
                [userAddr]: {
                  ...state.pairLeverage[chainId]?.[userAddr],
                  [pairId]: leverage,
                },
              },
            };
            return { pairLeverage: newPairLeverage };
          });
        },
        updatePairLeverage: (userAddr, chainId, pairId, leverage) => {
          set((state) => {
            const newPairLeverage = {
              ...state.pairLeverage,
              [chainId]: {
                ...state.pairLeverage[chainId],
                [userAddr]: {
                  ...state.pairLeverage[chainId]?.[userAddr],
                  [pairId]: leverage,
                },
              },
            };
            return { pairLeverage: newPairLeverage };
          });
        },
        // for scaleLimitForm
        sizeDistribution: BatchOrderSizeDistribution.FLAT,
        setSizeDistribution: (sizeDistribution) => set(() => ({ sizeDistribution })),
        upperPrice: '',
        setUpperPrice: (upperPrice) => set(() => ({ upperPrice })),
        alignedUpperPrice: '',
        setAlignedUpperPrice: (alignedUpperPrice: string) => set(() => ({ alignedUpperPrice })),
        lowerPrice: '',
        setLowerPrice: (lowerPrice) => set(() => ({ lowerPrice })),
        alignedLowerPrice: '',
        setAlignedLowerPrice: (alignedLowerPrice: string) => set(() => ({ alignedLowerPrice })),
        lowerTick: 0,
        setLowerTick: (lowerTick: number) => set(() => ({ lowerTick })),
        upperTick: 0,
        setUpperTick: (upperTick: number) => set(() => ({ upperTick })),
        orderCount: 5,
        setOrderCount: (orderCount: number) => set(() => ({ orderCount })),
        stepRatio: DEFAULT_STEP_RATIO,
        setStepRatio: (stepRatio: number) =>
          set(() => {
            return { stepRatio };
          }),
        pairStepRatioMap: {},
        setPairStepRatio: (pairKey: string, stepRatio: number) =>
          set((state) => {
            if (state.pairStepRatioMap[pairKey] === stepRatio && state.stepRatio === stepRatio) return state;
            return {
              stepRatio,
              pairStepRatioMap: { ...state.pairStepRatioMap, [pairKey]: stepRatio },
            };
          }),
        scaleLimitStepRatio: DEFAULT_STEP_RATIO,
        setScaleLimitStepRatio: (scaleLimitStepRatio: number) =>
          set(() => {
            return { scaleLimitStepRatio };
          }),
        pairScaleLimitStepRatioMap: {},
        setPairScaleLimitStepRatio: (pairKey: string, stepRatio: number) =>
          set((state) => {
            if (state.pairScaleLimitStepRatioMap[pairKey] === stepRatio && state.scaleLimitStepRatio === stepRatio)
              return state;
            return {
              scaleLimitStepRatio: stepRatio,
              pairScaleLimitStepRatioMap: { ...state.pairScaleLimitStepRatioMap, [pairKey]: stepRatio },
            };
          }),
        tradeType: TRADE_TYPE.LIMIT,
        setTradeType: (tradeType: TRADE_TYPE) => set(() => ({ tradeType })),
        toLimitTab: false,
        setToLimitTab: (fromOrderBook: boolean) => set(() => ({ toLimitTab: fromOrderBook })),
        fromOrderBook: false,
        setFromOrderBook: (fromOrderBook: boolean) => set(() => ({ fromOrderBook })),

        isOpenMobileTradeDrawer: false,
        setIsOpenMobileTradeDrawer: (isOpenMobileTradeDrawer: boolean) => set(() => ({ isOpenMobileTradeDrawer })),
      }),
      {
        name: 'trade-portfolio-store',
        partialize: (state) => ({
          hideOtherPair: state.hideOtherPair,
          // showTradeAreaOverlay: state.showTradeAreaOverlay,
          pairLeverage: state.pairLeverage,
          stepRatio: state.stepRatio,
          pairStepRatioMap: state.pairStepRatioMap,
          scaleLimitStepRatio: state.scaleLimitStepRatio,
          pairScaleLimitStepRatioMap: state.pairScaleLimitStepRatioMap,
          isTurnOnCrossMarket: state.isTurnOnCrossMarket,
          tradeType: state.tradeType,
        }),
      },
    ),
    {
      name: 'trade-portfolio-store',
    },
  ),
);
